'use client'

import { useMountEffect } from '@react-hookz/web'
import {
  Border1,
  ColorBaseTransparent,
  ColorBaseWhite,
  ColorBrandPrimary,
  ColorTextDark,
  SizeBorder1,
  SizeRadiusRounded,
  SizeRadiusS,
  SizeSpacing2,
  SizeSpacing3,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'

import { type ForwardedRef, forwardRef, useRef, useState } from 'react'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'

import { Apps } from '../../enums'
import {
  getSigninUrl,
  getSignupUrl,
  getUrl,
  usePathname,
} from '../../helpers/navigation'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import { asPx, spacings } from '../../styles/helpers'
import LegacyTable from '../LegacyTable'
import SubscribeButton from '../SubscribeButton'

export const TYPE = 'subscribe'

type Props = {
  variant: 'subscribe' | 'takeAction'
  editionUuid?: string
  forHtml?: boolean
  interactive?: boolean
  openInNewTab?: boolean
  withSignin?: boolean
}
const Subscribe = forwardRef<HTMLElement, Props>(
  (
    {
      editionUuid,
      forHtml = false,
      interactive = false,
      openInNewTab = false,
      variant = 'subscribe',
      withSignin = false,
    }: Props,
    _ref: ForwardedRef<HTMLElement>,
  ) => {
    const pathname = usePathname()
    const $t = useTranslations()

    const { subscribersNumber } = useFeatureFlags()

    const rootContainerRef = useRef<HTMLDivElement>(null)
    useMountEffect(() => {
      if (rootContainerRef.current) {
        rootContainerRef.current.setAttribute('contenteditable', 'false')
      }
    })

    const URLs = useUrls()
    const currentUrl =
      typeof window === 'undefined'
        ? `${URLs.media}${pathname}`
        : window.location.href

    const [, setIsFocusedOnSubscribe] = useState(false)

    const subscribeContent = (
      <>
        <Typography
          className={forHtml ? undefined : 'text-center'}
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'center' } : undefined}
          variant="heading-3"
        >
          ❄️
        </Typography>

        <Typography
          className={forHtml ? undefined : 'text-center'}
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'center' } : undefined}
          variant={forHtml ? 'heading-4' : 'heading-3'}
        >
          {$t('subscribe.block.title')}
        </Typography>

        <Typography
          className={forHtml ? undefined : '@sm:text-center'}
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'center' } : undefined}
          variant="body-1-median"
        >
          {$t('subscribe.block.details', {
            count: subscribersNumber,
          })}
        </Typography>
      </>
    )

    const paddingVertical = spacings[1] - spacings.quarter
    const minHeight =
      spacings[6] - paddingVertical * 2 - (asPx(SizeBorder1) as number) * 2

    const button = (
      <Button
        fullStyle={forHtml}
        href={getUrl(
          Apps.Media,
          '/snowball-plus',
          isEmpty(editionUuid)
            ? 'origin=plus_subscribe'
            : `from=${editionUuid}&source=${forHtml ? 'inbox' : 'media'}&origin=plus_subscribe`,
        )}
        inlineStyle={forHtml}
        style={
          forHtml
            ? {
                backgroundColor: ColorBaseTransparent,
                border: 'none',
                boxShadow: 'none',
                color: ColorTextDark,
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                minHeight: asPx(minHeight, { withUnit: true }),
                padding: 0,
                pointerEvents: 'auto',
              }
            : undefined
        }
        target={openInNewTab || forHtml ? '_blank' : undefined}
        variant="display"
        wrapperStyles={
          forHtml
            ? {
                marginTop: 0,
              }
            : undefined
        }
      >
        <Typography
          inlineStyle={forHtml}
          style={
            forHtml
              ? {
                  lineHeight: asPx(minHeight, { withUnit: true }),
                  maxWidth: '100%',
                  minHeight: asPx(minHeight, { withUnit: true }),
                  whiteSpace: 'break-spaces',
                }
              : undefined
          }
          variant="body-1-median"
        >
          {$t('take_action.cta.label')}
        </Typography>
      </Button>
    )

    const takeActionContent = (
      <>
        <Typography
          className={forHtml ? undefined : 'text-center'}
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'center' } : undefined}
          variant="heading-3"
        >
          ❄️
        </Typography>

        <Typography
          className={forHtml ? undefined : 'text-center'}
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'center' } : undefined}
          variant={forHtml ? 'heading-4' : 'heading-3'}
        >
          {$t.rich('take_action.title')}
        </Typography>

        <Typography
          className={forHtml ? undefined : 'text-left self-start'}
          element="div"
          inlineStyle={forHtml}
          style={forHtml ? { textAlign: 'left' } : undefined}
          variant="body-1-median"
        >
          {$t.rich('take_action.details')}
        </Typography>

        {forHtml ? (
          <LegacyTable
            autoWidth
            center
            defaultCellStyle={{
              backgroundColor: ColorBaseWhite,
              border: Border1,
              borderRadius: SizeRadiusRounded,
              borderWidth: asPx(SizeBorder1, { withUnit: true }),
              margin: '0 auto',
              maxWidth: '100%',
              padding: `${asPx(paddingVertical, { withUnit: true })} ${asPx(
                spacings[3] - spacings.quarter,
                { withUnit: true },
              )}`,
            }}
          >
            {button}
          </LegacyTable>
        ) : (
          button
        )}
      </>
    )

    return forHtml ? (
      <LegacyTable
        id={TYPE}
        col
        data-variant={variant}
        defaultCellStyle={{ textAlign: 'center' }}
        gap={spacings[3]}
        style={{
          backgroundColor: ColorBrandPrimary,
          border: 0,
          borderRadius: SizeRadiusS,
          marginBottom: asPx(SizeSpacing3, { withUnit: true }),
          padding: asPx(SizeSpacing2, { withUnit: true }),
        }}
      >
        {variant === 'subscribe' ? (
          <>
            {subscribeContent}

            <LegacyTable
              autoWidth
              center
              defaultCellStyle={{
                backgroundColor: ColorBaseWhite,
                border: Border1,
                borderRadius: SizeRadiusRounded,
                borderWidth: asPx(SizeBorder1, { withUnit: true }),
                margin: '0 auto',
                maxWidth: '100%',
                padding: `${asPx(paddingVertical, { withUnit: true })} ${asPx(
                  spacings[3] - spacings.quarter,
                  { withUnit: true },
                )}`,
              }}
            >
              <Button
                fullStyle={forHtml}
                href={getSignupUrl(
                  Apps.Media,
                  currentUrl,
                  isEmpty(editionUuid)
                    ? ''
                    : `from=${editionUuid}&source=inbox`,
                )}
                inlineStyle={forHtml}
                style={
                  forHtml
                    ? {
                        backgroundColor: ColorBaseTransparent,
                        border: 'none',
                        boxShadow: 'none',
                        color: ColorTextDark,
                        display: 'block',
                        margin: '0 auto',
                        maxWidth: '100%',
                        minHeight: asPx(minHeight, { withUnit: true }),
                        padding: 0,
                        pointerEvents: 'auto',
                      }
                    : undefined
                }
                target={openInNewTab || forHtml ? '_blank' : undefined}
                variant="display"
                wrapperStyles={
                  forHtml
                    ? {
                        marginTop: 0,
                      }
                    : undefined
                }
              >
                <Typography
                  inlineStyle={forHtml}
                  style={
                    forHtml
                      ? {
                          lineHeight: asPx(minHeight, { withUnit: true }),
                          maxWidth: '100%',
                          minHeight: asPx(minHeight, { withUnit: true }),
                          whiteSpace: 'break-spaces',
                        }
                      : undefined
                  }
                  variant="body-1-median"
                >
                  {$t('subscribe.cta.label')}
                </Typography>
              </Button>
            </LegacyTable>
          </>
        ) : (
          takeActionContent
        )}
      </LegacyTable>
    ) : (
      <div ref={rootContainerRef} className="w-full">
        <div
          id={TYPE}
          className="flex flex-col items-center gap-3 rounded-sm bg-primary p-3"
        >
          {variant === 'subscribe' && (
            <>
              {subscribeContent}

              {interactive ? (
                <SubscribeButton
                  app={Apps.Media}
                  editionUuid={editionUuid}
                  withForm
                  onStartSubscribe={() => setIsFocusedOnSubscribe(true)}
                  onSubmitted={() => setIsFocusedOnSubscribe(constant(true))}
                />
              ) : (
                <Button
                  href={getSignupUrl(
                    Apps.Media,
                    currentUrl,
                    isEmpty(editionUuid)
                      ? ''
                      : `from=${editionUuid}&source=media`,
                  )}
                  label={$t('subscribe.cta.label')}
                  target={openInNewTab ? '_blank' : undefined}
                  variant="display"
                />
              )}

              {withSignin && (
                <div className="flex w-full max-w-[480px] flex-col items-center">
                  <div>
                    <Typography element="span" variant="caption-median">
                      {$t('general.or')}
                    </Typography>
                    &nbsp;
                    <Typography
                      className="inline-block"
                      href={getSigninUrl(
                        Apps.Media,
                        currentUrl,
                        isEmpty(editionUuid) ? '' : `from=${editionUuid}`,
                      )}
                      target={openInNewTab ? '_blank' : undefined}
                      variant="caption-link"
                    >
                      {$t('subscribe.signin.label').toLocaleLowerCase()}
                    </Typography>
                  </div>
                </div>
              )}
            </>
          )}

          {variant === 'takeAction' && takeActionContent}
        </div>
      </div>
    )
  },
)
Subscribe.displayName = 'Subscribe'

export default Subscribe
